import {memo, useEffect} from 'react';

import {useDispatch} from 'react-redux';
import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {body14Normal, body16Normal, body20Bold, subheader24Bold} from '~/shared/theme/typography';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {media} from '~/shared/theme/media';
import {setIsAgeConfirmed} from '~/shared/store/actions';
import {trackEvent} from '~/shared/services/analytics';
import PieButton from '~/shared/theme/pie/PieButton';

import {PieCloseModalButton} from '../../styled';

import ageRestrictedImg from './assets/age-verification.png';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
  width: 100%;
  gap: 32px;

  &&& {
    background-color: ${({theme}) => theme.colors.contentWhite};
  }

  ${media.minLargeMobile`
    &&&{
      width: 500px;
    }
  `}
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 0 24px;
  width: 100%;
  ${media.minLargeMobile`
    margin-top: 88px;
    padding: 0 32px;
  `}
`;

const Img = styled(ImageWithAlt).attrs({
  src: ageRestrictedImg,
})`
  width: 150px;
  height: 150px;
  ${media.minLargeMobile`
    width: 200px;
    height: 200px;
  `}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #242e30;
`;

const Title = styled.h2`
  ${body20Bold}
  line-height: 28px;
  margin: 0;
  ${media.minLargeMobile`
    ${subheader24Bold}
    line-height: 32px;
  `}
`;

const Message = styled.p`
  ${body14Normal}
  line-height: 20px;
  margin: 0;
  ${media.minLargeMobile`
    ${body16Normal}
    line-height: 24px;
  `}
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 0 16px 16px;
  ${media.minLargeMobile`
    flex-direction: row;
    padding: 0 32px 24px 32px;
  `}
`;

const StyledButton = styled(PieButton)`
  width: 100%;
  ${media.minLargeMobile`
    flex: 1;
  `}
`;

interface AgeConfirmProps {
  onApprove: () => void;
  onCancel: () => void;
}

const AgeConfirm = ({onApprove, onCancel}: AgeConfirmProps) => {
  const dispatch = useDispatch();
  const {t} = getLocalizationService();
  const isMinLargeMobile = useIsMinLargeMobile();

  useEffect(() => {
    trackEvent('hasViewedAlcoholPopUp');
  }, []);

  const onApproveClick = () => {
    trackEvent('hasClickedAlcoholPopUp', {linkType: 'continue'});
    dispatch(setIsAgeConfirmed(true));
    onApprove();
  };

  const onCancelClick = () => {
    trackEvent('hasClickedAlcoholPopUp', {linkType: 'cancel'});
    onCancel();
  };

  return (
    <Root>
      <PieCloseModalButton onClick={onCancelClick} hideOnMobile/>
      <ContentSection>
        <Img noAlt />
        <TextContainer>
          <Title id="modal-title">{t('age_confirmation_modal_header')}</Title>
          <Message>{t('age_confirmation_modal_message')}</Message>
        </TextContainer>
      </ContentSection>
      <ButtonsContainer>
        <StyledButton variant={isMinLargeMobile ? 'primaryXL' : 'primaryLarge'} onClick={onApproveClick}>
          {t('age_confirmation_modal_confirm_button')}
        </StyledButton>
        <StyledButton variant={isMinLargeMobile ? 'secondaryXL' : 'secondaryLarge'} onClick={onCancelClick}>
          {t('cancel')}
        </StyledButton>
      </ButtonsContainer>
    </Root>
  );
};

export default memo(AgeConfirm);
